// Modified version of three.js OrbitControls

/// ////////////////////////////////////////
// Companion documentation can be found in
// /doc/orbitControls.md
/// ////////////////////////////////////////

import {
    EventDispatcher,
    MOUSE,
    Quaternion,
    Spherical,
    TOUCH,
    Vector2,
    Vector3,
    Clock,
    SphereGeometry,
    MeshBasicMaterial,
    Mesh,
    Object3D,
} from "three"

import {
    globeAzimuthalToLng,
    globePolarToLat,
    latToGlobePolar,
    lngToGlobeAzimuthal,
} from "./CameraSynchronizer"
import { DEG2RAD, RAD2DEG } from "three/src/math/MathUtils"

// This set of controls performs orbiting, dollying (zooming), and panning.
// Unlike TrackballControls, it maintains the "up" direction object.up (+Y by default).
//
//    Orbit - left mouse / touch: one-finger move
//    Zoom - middle mouse, or mousewheel / touch: two-finger spread or squish
//    Pan - right mouse, or left mouse + ctrl/meta/shiftKey, or arrow keys / touch: two-finger move

const _changeEvent = { type: "change" }
const _startEvent = { type: "start" }
const _endEvent = { type: "end" }

const STATE = {
    NONE: -1,
    ROTATE: 0,
    DOLLY: 1,
    PAN: 2,
    TOUCH_ROTATE: 3,
    TOUCH_PAN: 4,
    TOUCH_DOLLY_PAN: 5,
    TOUCH_DOLLY_ROTATE: 6,
    ANIMATING: 7,
    ANIMATION_ROTATE: 8,
    ANIMATION_FOCUS: 9,
}

/* Scratch objects (for memory optimization) */
const _scratchSpherical = new Spherical()

class GlobeControls extends EventDispatcher {
    constructor(camera, domElement, globe) {
        super()

        if (domElement === undefined)
            console.warn(
                'THREE.OrbitControls: The second parameter "domElement" is now mandatory.'
            )
        if (domElement === document)
            console.error(
                'THREE.OrbitControls: "document" should not be used as the target "domElement". Please use "renderer.domElement" instead.'
            )

        this.globe = globe
        this.camera = camera
        this.light = null
        this.domElement = domElement
        this.domElement.style.touchAction = "none" // disable touch scroll

        this.surfaceDist = 50

        // Set to false to disable this control
        this.enabled = true

        // Whether or not the camera is currently in a focusing maneuver
        this.isFocusing = false

        // "target" sets the location of focus, where the object orbits around
        this.target = new Vector3()

        // How far you can dolly in and out ( PerspectiveCamera only )
        this.minDistance = 0
        this.maxDistance = Infinity

        // How far you can zoom in and out ( OrthographicCamera only )
        this.minZoom = 0
        this.maxZoom = Infinity

        // The heigth at wich the camera is looking
        this.focusRadius = 51.5

        // How far you can orbit vertically, upper and lower limits.
        // Range is 0 to Math.PI radians.
        this.minPolarAngle = 0 // radians
        this.maxPolarAngle = Math.PI // radians

        // How far you can orbit horizontally, upper and lower limits.
        // If set, the interval [ min, max ] must be a sub-interval of [ - 2 PI, 2 PI ], with ( max - min < 2 PI )
        this.minAzimuthAngle = -Infinity // radians
        this.maxAzimuthAngle = Infinity // radians

        // How far the camera skews when zoomed in.
        this.minSkewAngle = 0
        this.maxSkewAngle = 0.4

        this.extraThetaOffset = Math.PI / 180

        // When in the track the camera rotates
        this.startRotatingInitialMouse = 0.7
        this.endRotatingInitialMouse = 0.9
        this.startRotatingInitialTouch = 0.45
        this.endRotatingInitialTouch = 1
        this.startRotating = this.startRotatingInitialMouse
        this.endRotating = this.endRotatingInitialMouse

        // How far the camera leans back when zoomed in.
        // This can be updated by updateMaxThetaOffset to precisely focus on a point at a certain height.
        // this.minThetaOffset = 0;
        // this.maxThetaOffset = 0.15;

        // Set to true to enable damping (inertia)
        // If damping is enabled, you must call controls.update() in your animation loop
        this.enableDamping = false
        this.dampingFactor = 0.1

        // This option actually enables dollying in and out; left as "zoom" for backwards compatibility.
        // Set to false to disable zooming
        this.enableZoom = true
        this.zoomSpeed = 1.0

        // Set to false to disable rotating
        this.enableRotate = true
        this.rotateSpeed = 1.0

        // Set to false to disable panning
        this.enablePan = true
        this.panSpeed = 1.0
        this.screenSpacePanning = true // if false, pan orthogonal to world-space direction camera.up
        this.keyPanSpeed = 7.0 // pixels moved per arrow key push

        // Set to true to automatically rotate around the target
        // If auto-rotate is enabled, you must call controls.update() in your animation loop
        this.autoRotate = false
        this.autoRotateSpeed = 2.0 // 30 seconds per orbit when fps is 60

        // For animations
        this.clock = new Clock()
        this.trackAnimation = {
            startTime: 0,
            endTime: 0,
            startValue: 0,
            endValue: 0,
        }

        this.trackPos = 0.13
        this.distance = 0

        this._sphericalDelta = new Spherical()
        // current position in spherical coordinates
        this._spherical = new Spherical()

        // Arcball ported members
        this._center = new Vector2()
        this._currentCursorPosition = new Vector3()
        this._startCursorPosition = new Vector3()
        this._currentCursorPositionFlick = new Vector3()
        this._startCursorPositionFlick = new Vector3()
        this._gizmoPosition = new Vector3(0, 50, 0)
        this._tbRadius = 50
        // Used for flick animation calculations
        this._flickTbRadius = 50

        this.radiusFactor = 0.67

        // Animation
        this.enableAnimations = true
        this._isFocusAnim = false
        this._isFocusTrack = false
        this._animationId = -1
        this._timeStart = -1
        this._timePrev = 0
        this._timeCurrent = 0
        // this._wHorizontalPrev = 0
        // this._wHorizontalCurr = 0
        // this._wVerticalPrev = 0
        // this._wVerticalCurr = 0
        this._wPrev = new Vector2()
        this._wCurr = new Vector2()
        this._wFlickPrev = new Vector2()
        this._wFlickCurr = new Vector2()
        this._cursorPosPrev = new Vector3()
        this._cursorPosCurr = new Vector3()
        this.wMax = 0.1 // Max angular velocity allowed
        this.flickDeadZoneRadius = 0.01
        this.tbDampingFactor = 0.2
        this.focusDuration = 0.5 // Seconds

        // Globe info
        this._lngLat = new Vector2()

        // const templateAnimation = {
        // 	propertyName: "trackPos",
        // 	startTime: 0,
        // 	endTime: 0,
        // 	startValue: 0,
        // 	endValue: 0
        // }

        this.animations = []

        // The four arrow keys
        this.keys = {
            LEFT: "ArrowLeft",
            UP: "ArrowUp",
            RIGHT: "ArrowRight",
            BOTTOM: "ArrowDown",
        }

        // Mouse buttons
        this.mouseButtons = {
            LEFT: MOUSE.ROTATE,
            MIDDLE: MOUSE.DOLLY,
            RIGHT: MOUSE.PAN,
        }

        // Touch fingers
        this.touches = { ONE: TOUCH.ROTATE, TWO: TOUCH.DOLLY_ROTATE }

        // for reset
        this.target0 = this.target.clone()
        this.position0 = this.camera.position.clone()
        this.zoom0 = this.camera.zoom

        // the target DOM element for key events
        this._domElementKeyEvents = null

        // Workaround for the imperfect zoom algorithm
        // TODO: For increased accuracy, the bouning sphere of the actual mesh we're zooming on needs to be used
        // 	Currently the lower-level mesh is used because that's what's already being used and because it's a single mesh
        // 	whereas the new object being loaded in is a collection of meshes which will be harder to get an overall bounding sphere for
        this.focusMeshOffsets = new Map()

        // Add hard-coded offests
        this.focusMeshOffsets.set("MA", {
            radius: 0.9,
            phi: 0.01,
            theta: 0.016,
        })

        // Scratch objects (for memory optimization)
        this._v2_1 = new Vector2()
        this._v2_2 = new Vector2()
        this._v3_1 = new Vector3()
        this._v3_2 = new Vector3()

        //
        // public methods
        //

        /**
         * Perform animation for rotation operation
         * @param {Number} time Instant in which this function is called as performance.now()
         * @param {Number} angularSpeed0 Initial angular speed
         * @param {Vector2} direction Direction of spin
         * @param {Number} flickSpeed0 speed of the flick gesture in flick space
         */
        this.onRotationAnim = function (
            time,
            angularSpeed0,
            direction,
            flickSpeed0
        ) {
            if (this._timeStart === -1) {
                // animation start
                // this._anglePrev = 0;
                this._angleCurrent = 0
                this._timeStart = time
            }

            if (this._state === STATE.ANIMATION_ROTATE) {
                // w = w0 + alpha * t
                const deltaTime = (time - this._timeStart) / 1000
                // const wHorizontal = w0Horizontal - (w0Horizontal * (this.dampingFactor))
                // const wVertical = w0Vertical - (w0Vertical * (this.dampingFactor))

                // TODO: change for vector and diminish magnitude

                const flickSpeedToAngularSpeed = angularSpeed0 / flickSpeed0

                const w = flickSpeed0 + -this.tbDampingFactor * deltaTime

                // const wHorizontal = w0Horizontal + ( ( - this.tbDampingFactor ) * deltaTime );
                // const wVertical = w0Vertical + ( ( - this.tbDampingFactor ) * deltaTime );

                if (w > 0) {
                    // tetha = 0.5 * alpha * t^2 + w0 * t + tetha0
                    // const horizontalCurrent = 0.5 * ( - this.tbDampingFactor ) * Math.pow( deltaTime, 2 ) + w0Horizontal * deltaTime + 0;
                    // const verticalCurrent = 0.5 * ( - this.tbDampingFactor ) * Math.pow( deltaTime, 2 ) + w0Vertical * deltaTime + 0;

                    // const horizontalCurrent = w0.x - (this.tbDampingFactor * deltaTime)
                    // const verticalCurrent = w0.y - (this.tbDampingFactor * deltaTime)

                    this._v2_1
                        .copy(direction)
                        .multiplyScalar(w * flickSpeedToAngularSpeed)

                    // console.log(wHorizontal)
                    // console.log(horizontalCurrent)

                    // this.applyTransformMatrix( this.rotate( rotationAxis, this._angleCurrent ) );
                    this.rotateLeft(this._v2_1.x)
                    this.rotateUp(this._v2_1.y)
                    this.dispatchEvent(_changeEvent)
                    const self = this
                    this._animationId = window.requestAnimationFrame(function (
                        t
                    ) {
                        self.onRotationAnim(
                            t,
                            angularSpeed0,
                            direction,
                            flickSpeed0
                        )
                    })
                } else {
                    this._animationId = -1
                    this._timeStart = -1

                    this.updateTbState(STATE.NONE, false)
                    // this.activateGizmos( false );

                    this.dispatchEvent(_changeEvent)
                }
            } else {
                // interrupt animation

                this._animationId = -1
                this._timeStart = -1

                if (this._state !== STATE.ROTATE) {
                    // this.activateGizmos( false );
                    this.dispatchEvent(_changeEvent)
                }
            }
        }

        // Arcball methods
        /**
         * Set _center's x/y coordinates
         * @param {Number} clientX
         * @param {Number} clientY
         */
        this.setCenter = function (clientX, clientY) {
            this._center.x = clientX
            this._center.y = clientY
        }

        // Drag surface should be set to the distance from the origin to the surface we're currently dragging.
        // This changes depending on whether we're dragging the globe itself, a state, a zip, etc.
        this.setDragSurfaceDist = function (dist) {
            this.surfaceDist = dist
        }

        // Updates the amount that the camera slides back when fully zoomed in
        // based on the skew angle, minDistance, and the radius of the focus point
        // This should be called any time maxSkewAngle or minDistance change.
        this.calcThetaOffset = (function () {
            const piOver2 = Math.PI / 2
            // const twoPi = Math.PI * 2

            return function (angle, doc, dog, out) {
                // doc = distanceObjectToCamera;
                // dog = distanceObjectToGlobeCenter;

                // I have a piece of paper that explains why this works.
                // Too bad code comments can't contain images (just kidding, awful idea.)
                // Law of cosines: a^2 = b^2 + c^2 - 2ac*cos(A)

                const sinB = Math.sin(angle)

                let A = Math.asin((doc * sinB) / dog)

                let H = (dog * Math.sin(Math.PI - angle - A)) / sinB

                // Resolve the ambiguous case - A should never be > 90 degrees
                if (A > piOver2) {
                    // Find the complement
                    A = Math.PI - A
                }

                if (H === Infinity) {
                    H = doc + dog
                }

                out[0] = A
                out[1] = H
                out[2] = angle + A
                return out

                // console.log('maxThetaOffset', scope.maxThetaOffset);
            }
        })()

        this.getMeshDistFromGlobeCenter = (function () {
            return function getMeshDistFromGlobeCenter(mesh) {
                // TODO: To make this more portable, a bounding box center could be used instead of an anchor.
                const anchor = mesh.children[0]

                // TODO: pull this from the globe center
                const toCenter = new Vector3(0, 50, 0)
                const out = new Vector3().copy(anchor.position)
                anchor.parent.updateWorldMatrix(true)
                anchor.parent.localToWorld(out)

                toCenter.sub(out)

                return toCenter.length()
            }
        })()

        this.onWindowResize = function () {
            this.vDistanceCoefficient = Math.sqrt(
                Math.pow(
                    Math.cos((camera.fov * Math.PI) / 180 / 2) /
                        Math.sin((camera.fov * Math.PI) / 180 / 2),
                    2
                ) + 1
            )
            this.horizontalFov =
                (2 *
                    Math.atan(
                        Math.tan((camera.fov * Math.PI) / 180 / 2) *
                            camera.aspect
                    ) *
                    180) /
                Math.PI // degrees
            this.hDistanceCoefficient = Math.sqrt(
                Math.pow(
                    Math.cos((this.horizontalFov * Math.PI) / 180 / 2) /
                        Math.sin((this.horizontalFov * Math.PI) / 180 / 2),
                    2
                ) + 1
            )
            this._screenDependantTbRadius = calculateTbRadius(
                this.camera,
                this._gizmoPosition,
                this.radiusFactor
            )
        }

        this.onWindowResize()

        this.focusLngLat = function (lng, lat, duration) {
            scope.focus(lng, lat)
            scope.trackInSlow(1, duration)
        }

        this.focusMesh = (function () {
            const target = new Vector3()
            const tmpSpherical = new Spherical()

            // TODO: Pull this from the actual globe origin instead of hardcoding
            const globeOrigin = new Vector3(0, 50, 0)

            // const twopi = Math.PI * 2

            return function focusMesh(
                mesh,
                duration = 1,
                changeRotation = true,
                usingMinDistance = 53.2,
                distanceScalar = 1.02
            ) {
                // Process parameters
                if (!usingMinDistance) {
                    usingMinDistance = scope.minDistance
                }

                mesh.updateWorldMatrix(true)

                // Get the mesh pos and then focus it
                // State meshes are all positioned at 0, so we need to get a bounding box center instead.

                // Get the world pos of the center of the bounding sphere
                const geometry = mesh.geometry
                geometry.computeBoundingSphere()
                target.copy(geometry.boundingSphere.center)
                mesh.localToWorld(target)

                // Extract scale from mesh matrixWorld
                const scale = new Vector3()
                {
                    const position = new Vector3()
                    const quaternion = new Quaternion()
                    mesh.matrixWorld.decompose(position, quaternion, scale)
                }

                // Add guide sphere
                const sphereGeometry = new SphereGeometry(
                    scale.x * geometry.boundingSphere.radius,
                    32,
                    32
                )
                const material = new MeshBasicMaterial({
                    color: "#00FF00",
                    wireframe: true,
                })
                const sphere = new Mesh(sphereGeometry, material)
                sphere.position.copy(target)
                const obj = new Object3D()
                obj.position.set(0, 50, 0)
                obj.visible = false
                obj.attach(sphere)

                if (mesh.userData.clickedScale)
                    obj.scale.multiplyScalar(
                        mesh.userData.clickedScale.x / mesh.scale.x
                    )

                camera.parent.add(obj)

                sphere.updateWorldMatrix(true)
                const target2 = new Vector3()
                sphere.localToWorld(target2)
                target2.sub(new Vector3(0, 50, 0))

                // Calculate focus radius based on one vertex on the face of the region
                const positionAttribute = geometry.getAttribute("position")
                const vertex = new Vector3()
                vertex.fromBufferAttribute(positionAttribute, 0)
                mesh.localToWorld(vertex)
                vertex.sub(new Vector3(0, 50, 0))

                const meshSurfaceDistance =
                    vertex.length() *
                    (mesh.userData.clickedScale.x / mesh.scale.x)

                // Check for workaround offset
                let offset
                if (this.focusMeshOffsets.has(mesh.parent.abbr)) {
                    offset = this.focusMeshOffsets.get(mesh.parent.abbr)

                    // Apply offset to the mesh focus position
                    tmpSpherical.setFromVector3(target.sub(globeOrigin))
                    console.log(
                        "Focus mesh before",
                        target,
                        tmpSpherical,
                        offset
                    )
                    tmpSpherical.theta = tmpSpherical.theta + offset.theta // % twopi;
                    tmpSpherical.phi = tmpSpherical.phi + offset.phi // % twopi;
                    tmpSpherical.radius += offset.radius
                    target.setFromSpherical(tmpSpherical)
                    console.log("Focus mesh after", target, tmpSpherical)

                    // Convert back to world pos
                    target.add(globeOrigin)
                }

                // End workaround

                // scope.isFocusing = true
                scope.setFocusingTrackFlag(true)
                scope.focus(target)

                let distanceCoefficient = this.vDistanceCoefficient

                if (camera.fov > this.horizontalFov) {
                    distanceCoefficient = this.hDistanceCoefficient
                    console.log("HORIZONTAL CHOSEN")
                }

                // Assumes scale is uniform
                const meshSizeScalar = scale.x
                let framingDistance =
                    geometry.boundingSphere.radius *
                        distanceCoefficient *
                        meshSizeScalar *
                        distanceScalar +
                    meshSurfaceDistance

                if (offset) framingDistance += offset.radius

                let trackTarget = Math.max(
                    0,
                    Math.min(
                        1,
                        scope.invertCameraTrack(
                            framingDistance,
                            usingMinDistance
                        )
                    )
                )
                if (isNaN(trackTarget)) {
                    trackTarget = 1
                }

                if (changeRotation) {
                    const endRotating = trackTarget
                    const startRotating = trackTarget - 0.2

                    // animate start and end rotating
                    scope.animateTo("endRotating", endRotating, 1)

                    scope.animateTo("startRotating", startRotating, 1)
                }
                scope.trackInSlow(trackTarget, duration)
            }
        })()

        this.setExtraThetaOffset = function (targetExtraThetaOffset) {
            extraThetaOffsetDelta =
                targetExtraThetaOffset - this.extraThetaOffset
        }

        this.setMinDistance = function (targetMinDistance) {
            minDistanceDelta = targetMinDistance - this.minDistance
        }

        this.setFocusRadius = function (targetFocusRadius) {
            focusRadiusDelta = targetFocusRadius - this.focusRadius
        }

        this.jumpToFocusRadius = function (radius) {
            this.focusRadius = radius
        }

        this.focusZoom = (function () {
            // const target = new Vector3()

            return function focusZoom(pos) {
                scope.focus(pos)
                pos.sub(scope.target)
                const dist = pos.length()
                scope.focusRadius = dist
                // scope.updateMaxThetaOffset(dist);
                scope.trackInSlow(1, 2)
            }
        })()

        // Zoom in slowly using the animation system
        this.trackInSlow = (function () {
            return function trackInSlow(amount, duration) {
                if (amount === 0) return

                // const startTime = scope.clock.getElapsedTime()

                // const animation = {
                //     propertyName: "trackPos",
                //     startValue: Math.max(0, Math.min(1, scope.trackPos)),
                //     endValue: Math.max(0, Math.min(1, amount)),
                //     startTime,
                //     endTime: startTime + duration,
                // }

                scope.startAnimation(
                    "trackPos",
                    Math.max(0, Math.min(1, scope.trackPos)),
                    Math.max(0, Math.min(1, amount)),
                    duration
                )
            }
        })()

        this.animateTo = function (propertyName, endValue, duration) {
            scope.startAnimation(
                propertyName,
                scope[propertyName],
                endValue,
                duration
            )
        }

        this.startAnimation = function (
            propertyName,
            startValue,
            endValue,
            duration
        ) {
            const startTime = scope.clock.getElapsedTime()

            const animation = {
                propertyName,
                startValue,
                endValue,
                startTime,
                endTime: startTime + duration,
            }

            // Maybe we should clear the animation list here if state isn't animating so we clear old animations that were cancelled by a mouse movement
            this.animations.push(animation)
            // scope._state = STATE.ANIMATING;
        }

        this.abortAnimations = function () {
            this.animations = []
        }

        // this.startAnimation = function(animation) {
        // 	// Maybe we should clear the animation list here if state isn't animating so we clear old animations that were cancelled by a mouse movement
        // 	this.animations.push(animation);
        // 	state = STATE.ANIMATING;
        // };

        // Carry out animations
        this.animate = function () {
            // if (scope._state !== STATE.ANIMATING
            // 	&& scope._state !== STATE.ANIMATION_FOCUS
            // )
            // 	return;

            if (this.animations.length === 0) return

            const time = this.clock.getElapsedTime()

            for (let i = 0; i < this.animations.length; i++) {
                const animation = this.animations[i]
                // console.log('carrying out', animation);

                // Check for expired animations
                if (time >= animation.endTime) {
                    // Set our animated property to the end value (could represent a half-frame of movement)
                    this[animation.propertyName] = animation.endValue

                    // Remove this animation from the array
                    this.animations.splice(i, 1)

                    if (this.animations.length === 0) {
                        // No animations left, so reset our state and short circuit
                        // scope._state = STATE.NONE;
                        return
                    }

                    // Decrement the i so next loop starts in the right place
                    i--
                }

                // Carry out any animations in progress
                const progress =
                    (time - animation.startTime) /
                    (animation.endTime - animation.startTime)
                const sq = progress * progress
                const curvedProgress = sq / (2 * (sq - progress) + 1)
                this[animation.propertyName] =
                    curvedProgress * animation.endValue +
                    (1 - curvedProgress) * animation.startValue
            }
        }

        this.focus = (function () {
            const focusSpherical = new Spherical()
            const offset = new Vector3()
            // const deltaPhi = 0;
            // const deltaTheta = 0;

            function focusVector3(vec, { animated } = { animated: true }) {
                // We want the spherical to be relative to the center of the globe, i.e. scope.target
                offset.copy(vec).sub(scope.target)
                focusSpherical.setFromVector3(offset)
                focusSpherical.makeSafe()

                if (animated) {
                    const start = new Vector2(
                        scope._spherical.theta,
                        scope._spherical.phi
                    )
                    const end = new Vector2(
                        focusSpherical.theta,
                        focusSpherical.phi
                    )

                    const self = scope
                    scope._animationId = window.requestAnimationFrame(function (
                        t
                    ) {
                        self.updateTbState(STATE.ANIMATION_FOCUS, true)
                        self.onFocusAnim(t, start, end)
                    })
                } else {
                    scope._spherical.phi = focusSpherical.phi
                    scope._spherical.theta = focusSpherical.theta
                }

                console.log("focusing", offset)
            }

            function focusLngLat(lng, lat, { animated } = { animated: false }) {
                const polar = latToGlobePolar(lat)
                const azimuthal = lngToGlobeAzimuthal(lng)

                if (animated) {
                    const start = new Vector2(
                        scope._spherical.theta,
                        scope._spherical.phi
                    )
                    const end = new Vector2(azimuthal, polar)
                    const self = scope
                    scope._animationId = window.requestAnimationFrame(function (
                        t
                    ) {
                        self.updateTbState(STATE.ANIMATION_FOCUS, true)
                        self.onFocusAnim(t, start, end)
                    })
                } else {
                    scope._spherical.phi = polar
                    scope._spherical.theta = azimuthal
                }
            }

            /**
             * @overload
             * @param {Vector3} input
             * @param {{animated: boolean}} [options]
             */
            /**
             * @overload
             * @param {!number} lat
             * @param {!number} lng
             * @param {{animated: boolean}} [options]
             */
            return function focus(input, options) {
                if (typeof input === "object" && input.isVector3) {
                    focusVector3(input, options)
                } else if (
                    typeof input === "object" ||
                    typeof input === "number"
                ) {
                    focusLngLat(...arguments)
                }
            }
        })()

        this.setLight = function (light, sun) {
            this.light = light
            this.sun = sun
            this.lightDistance = this.light.position.distanceTo(this.target)
        }

        // Return the track value you need to get a certain distance
        this.invertCameraTrack = (function () {
            return function invertCameraTrack(distance, minDistance) {
                let endDist = scope.minDistance
                if (!(minDistance === undefined)) {
                    endDist = minDistance
                }

                const startDist = scope.maxDistance
                // const endDist = scope.minDistance;
                const invLerp = (distance - startDist) / (endDist - startDist)

                // console.log("invLerp is " + invLerp);

                const t1 = 1 + Math.sqrt(4 - 4 * invLerp) / -2
                const t2 = 1 + Math.sqrt(4 - 4 * t1) / -2

                return t2
            }
        })()

        this.cameraTrack = (function () {
            const out = []

            // const extraThetaOffset = Math.PI / 180;
            const maxExtraOffset = (3 * Math.PI) / 180
            return function cameraTrack(t, data) {
                // Inverse T
                // distance = startDist * (1 - tCurved) + endDist * tCurved
                // tCurved = (distance - startDist) / (endDist - startDist)
                // tCurved = 2*t * (1 - 0.5 * t);
                // 2*t * (1 - 0.5 * t) = (distance - startDist) / (endDist - startDist)
                // 2t - t**2 = (distance - startDist) / (endDist - startDist)
                // -t**2 + 2t - (distance - startDist) / (endDist - startDist) = 0
                // (distance - startDist) / (endDist - startDist) = invLerp

                // -t**2 + 2t - invLerp = 0

                // a =-1
                // b = 2
                // c = -invLerp

                // quadratic formula ->
                // t = 1 +- (sqrt(4 - 4*invLerp)) / -2;

                // t = -1 * (t * t) + 2 * t;

                // console.log(t);

                // Clamp betweeo 0 and 1
                t = Math.min(1, Math.max(0, t))

                const startDist = scope.maxDistance
                const endDist = scope.minDistance

                // const totalDistance = scope.maxDistance - scope.minDistance
                // console.log(startDist, endDist, t);
                // const startRotating = 0.7
                // const endRotating = 0.9 ;//- (10 / totalDistance);
                const totalRotating = scope.endRotating - scope.startRotating
                const startAngle = scope.minSkewAngle
                const endAngle = scope.maxSkewAngle

                // TODO: This needs to vary with zoom level to accurately zoom to the surface of a state
                // const startTheta = scope.minThetaOffset;
                // const endTheta = scope.maxThetaOffset;

                const angleT = Math.min(
                    1,
                    Math.max(0, (t - scope.startRotating) / totalRotating)
                )
                const angleTSquared = angleT * angleT
                const angleTCurved =
                    angleTSquared / (2 * (angleTSquared - angleT) + 1)

                // t overall curve
                t = -1 * (t * t) + 2 * t

                const tCurved = 2 * t * (1 - 0.5 * t)

                // lerps
                const distance = Math.min(
                    400,
                    startDist * (1 - tCurved) + endDist * tCurved
                )
                const angle =
                    startAngle * (1 - angleTCurved) + endAngle * angleTCurved

                // Calculate theta offset based on angle and distance
                scope.calcThetaOffset(
                    angle,
                    distance - scope.focusRadius,
                    scope.focusRadius,
                    out
                )
                extraThetaOffset = Math.min(
                    maxExtraOffset,
                    scope.extraThetaOffset *
                        (scope.getDistance() - scope.focusRadius)
                )
                const thetaOffset = out[0] + extraThetaOffset
                const offsetDistance = out[1]
                // console.log(scope.getDistance())
                // console.log(Math.min(maxExtraOffset, extraThetaOffset * (50 - scope.getDistance())))

                // console.log(angleT, t, startRotating, totalRotating);

                // distance = Math.min(distance, 400);

                if (data === null || data === undefined) {
                    data = {}
                }

                data.distance = distance
                data.angle = angle
                data.thetaOffset = thetaOffset
                data.offsetDistance = offsetDistance

                return data

                // console.log(data);
            }
        })()

        this.getPolarAngle = function () {
            return scope._spherical.phi
        }

        this.setPolarAngle = function (angle) {
            scope._spherical.phi = angle
        }

        this.getExtraThetaOffset = function () {
            return extraThetaOffset
        }

        this.getAzimuthalAngle = function () {
            return scope._spherical.theta
        }

        this.setAzimuthalAngle = function (angle) {
            scope._spherical.theta = angle
        }

        this.getDistance = function () {
            // return this.object.position.distanceTo( this.target );
            return this.distance
        }

        this.listenToKeyEvents = function (domElement) {
            domElement.addEventListener("keydown", onKeyDown)
            this._domElementKeyEvents = domElement
        }

        this.saveState = function () {
            scope.target0.copy(scope.target)
            scope.position0.copy(scope.camera.position)
            scope.zoom0 = scope.camera.zoom
        }

        this.reset = function () {
            scope.target.copy(scope.target0)
            scope.camera.position.copy(scope.position0)
            scope.camera.zoom = scope.zoom0

            scope.camera.updateProjectionMatrix()
            scope.dispatchEvent(_changeEvent)

            scope.update()

            scope._state = STATE.NONE
        }

        this.getTrackData = function () {
            return trackData
        }

        // this method is exposed, but perhaps it would be better if we can make it private...
        this.update = (function () {
            const offset = new Vector3()
            const toTarget = new Vector3()
            const axis = new Vector3()
            const targetOffset = new Vector3()
            // const sunTarget = new Vector3()

            const lightSpherical = new Spherical()
            const lightOffset = new Vector3()
            const offsetSpherical = new Spherical()

            // so camera.up is the orbit axis
            const quat = new Quaternion().setFromUnitVectors(
                camera.up,
                new Vector3(0, 1, 0)
            )
            const quatInverse = quat.clone().invert()

            const lastPosition = new Vector3()
            const lastQuaternion = new Quaternion()

            const twoPI = 2 * Math.PI

            return function update() {
                scope.animate()

                // Clamp the track position between 0 and 1
                scope.trackPos = Math.min(1, Math.max(0, scope.trackPos))
                // const trackPosCurved = Math.sqrt(scope.trackPos)
                scope.cameraTrack(scope.trackPos, trackData)
                this.distance = trackData.distance

                const position = scope.camera.position

                offset.copy(position).sub(scope.target)

                // rotate offset to "y-axis-is-up" space
                offset.applyQuaternion(quat)

                // angle from z-axis around y-axis
                // spherical.setFromVector3( offset );

                if (scope.autoRotate && scope._state === STATE.NONE) {
                    scope.rotateLeft(getAutoRotationAngle())
                }

                if (scope.enableDamping) {
                    scope.minDistance += minDistanceDelta * scope.dampingFactor
                    scope.focusRadius += focusRadiusDelta * scope.dampingFactor
                    scope.extraThetaOffset +=
                        extraThetaOffsetDelta * scope.dampingFactor
                    // if ( minDistanceDelta != 0 ) {
                    // 	scope.updateMaxThetaOffset();
                    // }
                } else {
                    scope.minDistance += minDistanceDelta
                    scope.focusRadius += focusRadiusDelta
                    scope.extraThetaOffset += extraThetaOffsetDelta
                }

                scope._spherical.theta += scope._sphericalDelta.theta
                scope._spherical.phi += scope._sphericalDelta.phi

                // restrict theta to be between desired limits

                let min = scope.minAzimuthAngle
                let max = scope.maxAzimuthAngle

                if (isFinite(min) && isFinite(max)) {
                    if (min < -Math.PI) min += twoPI
                    else if (min > Math.PI) min -= twoPI

                    if (max < -Math.PI) max += twoPI
                    else if (max > Math.PI) max -= twoPI

                    if (min <= max) {
                        scope._spherical.theta = Math.max(
                            min,
                            Math.min(max, scope._spherical.theta)
                        )
                    } else {
                        scope._spherical.theta =
                            scope._spherical.theta > (min + max) / 2
                                ? Math.max(min, scope._spherical.theta)
                                : Math.min(max, scope._spherical.theta)
                    }
                }

                // This is to prevent the bug where the globe flips upside down near antarctica)
                scope.maxPolarAngle =
                    Math.PI - (trackData.angle + trackData.thetaOffset + 0.1)

                // restrict phi to be between desired limits
                scope._spherical.phi = Math.max(
                    scope.minPolarAngle,
                    Math.min(scope.maxPolarAngle, scope._spherical.phi)
                )

                scope._spherical.makeSafe()

                // if (isNaN(trackPos))
                // 	trackPos = 0.32;

                // if (isNaN(scope.))

                if (scope.enableDamping) {
                    scope.trackPos += trackDelta * scope.dampingFactor

                    // Only apply "mouse zoom center" calculations for zooming in with mouse
                    if (trackDelta > 0.0000000001 && pointers.length === 0) {
                        // TODO: Change _spherical based on how much trackDelta changed and current mouse position
                        const tmpTrackData = { distance: 0 }
                        scope.cameraTrack(scope.trackPos, tmpTrackData)

                        // Get mouse LL before zoom
                        // TODO: Get this pre-calculated?
                        scope._v3_1.copy(
                            unprojectOnTbSurface(
                                scope.camera.projectionMatrixInverse,
                                scope.camera.type,
                                trackData.distance,
                                scope._center.x,
                                scope._center.y,
                                scope.domElement,
                                scope._tbRadius,
                                scope._gizmoPosition
                            )
                        )

                        // scope._v3_2.copy(scope._v3_1).normalize()

                        // console.log([scope._v3_2.x, scope._v3_2.y, scope._v3_2.z])

                        // scope._v3_2.setFromSpherical(scope._spherical).normalize()

                        // console.log([scope._v3_2.x, scope._v3_2.y, scope._v3_2.z])

                        const lng = globeAzimuthalToLng(scope._spherical.theta)
                        const lat = globePolarToLat(
                            scope._spherical.phi,
                            scope.getExtraThetaOffset()
                        )

                        // v2_1 is lng/lat of camera
                        scope._v2_1.set(lng, lat)

                        // v2_2 is lng/lat of mouse
                        arcballPosToMouseLngLat(scope._v3_1, scope._v2_1, {
                            tmpSpherical: _scratchSpherical,
                            extraThetaOffset: scope.getExtraThetaOffset(),
                            out: scope._v2_2,
                        })

                        // console.log(this._v2_2, this._lngLat)

                        // Get trackball position after zoom
                        scope._v3_2.copy(
                            unprojectOnTbSurface(
                                scope.camera.projectionMatrixInverse,
                                scope.camera.type,
                                tmpTrackData.distance,
                                scope._center.x,
                                scope._center.y,
                                scope.domElement,
                                scope._tbRadius,
                                scope._gizmoPosition
                            )
                        )

                        // v2_1 new lng/lat of camera
                        arcballPosToCameraLngLat(scope._v3_2, scope._v2_2, {
                            tmpSpherical: _scratchSpherical,
                            extraThetaOffset: scope.getExtraThetaOffset(),
                            out: this._v2_1,
                        })

                        const theta = lngToGlobeAzimuthal(this._v2_1.x)
                        const phi = latToGlobePolar(
                            this._v2_1.y,
                            scope.getExtraThetaOffset()
                        )

                        this._spherical.theta = theta
                        this._spherical.phi = phi
                    }

                    // const change = tmpTrackData.distance - trackData.distance
                    // const mouseAzi = lngToGlobeAzimuthal(scope._lngLat.x)
                    // const mousePolar = latToGlobePolar(scope._lngLat.y)

                    // scope._v2_1.set(mouseAzi, mousePolar)
                    // scope._v2_2.set(
                    //     scope._spherical.theta,
                    //     scope._spherical.phi
                    // )
                    // scope._v2_2.sub(scope._v2_1)

                    // scope._spherical.theta += scope._v2_2.x * change * 0.05 //* (trackData.distance - this._tbRadius)
                    // scope._spherical.phi += scope._v2_2.y * change * 0.05 //* (trackData.distance - this._tbRadius)
                } else {
                    scope.trackPos += trackDelta
                }

                // console.log(trackData);

                scope._spherical.radius = trackData.offsetDistance // (scale - 1) * 100;

                // restrict radius to be between desired limits
                scope._spherical.radius = Math.max(
                    scope.minDistance,
                    Math.min(scope.maxDistance, scope._spherical.radius)
                )

                // move target to panned location

                if (scope.enableDamping === true) {
                    scope.target.addScaledVector(panOffset, scope.dampingFactor)
                } else {
                    scope.target.add(panOffset)
                }

                offsetSpherical.copy(scope._spherical)
                offsetSpherical.phi += trackData.thetaOffset

                // restrict phi to be between desired limits
                offsetSpherical.phi = Math.max(
                    scope.minPolarAngle,
                    Math.min(scope.maxPolarAngle, offsetSpherical.phi)
                )

                offset.setFromSpherical(offsetSpherical)

                // rotate offset back to "camera-up-vector-is-up" space
                offset.applyQuaternion(quatInverse)

                position.copy(scope.target).add(offset)

                if (trackData.angle > 0) {
                    toTarget.copy(scope.target).sub(scope.camera.position)
                    axis.crossVectors(toTarget, scope.camera.up).normalize()
                    targetOffset
                        .copy(toTarget)
                        .applyAxisAngle(axis, trackData.angle)
                        .add(scope.camera.position)
                    scope.camera.lookAt(targetOffset)
                } else {
                    scope.camera.lookAt(scope.target)
                }

                if (scope.light) {
                    lightSpherical.copy(scope._spherical)
                    lightSpherical.radius = scope.lightDistance
                    lightOffset.setFromSpherical(lightSpherical)
                    scope.light.position.copy(scope.target).add(lightOffset)
                    lightSpherical.radius = 50
                    lightOffset.setFromSpherical(lightSpherical)
                    // console.log(lightOffset);
                    // sunTarget.copy(scope.target).add(lightOffset);
                    scope.sun.target.position
                        .copy(scope.target)
                        .add(lightOffset)

                    scope.light.lookAt(scope.target)

                    // Shrink shadow camera bounds based on distance to maintain shadow quality
                    const dist = scope.getDistance() - 50
                    let bound = dist * 1
                    bound = Math.min(40, bound)
                    scope.sun.shadow.camera.top = -bound
                    scope.sun.shadow.camera.left = -bound
                    scope.sun.shadow.camera.right = bound
                    scope.sun.shadow.camera.bottom = bound
                    // scope.sun.shadow.camera.far = bound + 260;
                    scope.sun.shadow.camera.updateProjectionMatrix()

                    if (scope.sun.userData.helper) {
                        scope.sun.userData.helper.update()
                    }

                    scope.sun.shadow.bias = (-0.0005 * (dist + 1)) / 50

                    // console.log(bound);
                }

                if (scope.enableDamping === true) {
                    panOffset.multiplyScalar(1 - scope.dampingFactor)

                    trackDelta *= 1 - scope.dampingFactor

                    minDistanceDelta *= 1 - scope.dampingFactor
                    focusRadiusDelta *= 1 - scope.dampingFactor

                    extraThetaOffsetDelta *= 1 - scope.dampingFactor

                    // Test if focus manuever is over
                    if (scope._isFocusTrack) {
                        const threshold = 0.00000000001
                        if (Math.abs(trackDelta) < threshold) {
                            scope.setFocusingTrackFlag(false)
                        }
                    }
                } else {
                    panOffset.set(0, 0, 0)

                    trackDelta = 0

                    minDistanceDelta = 0
                    focusRadiusDelta = 0

                    extraThetaOffsetDelta = 0
                }

                scope._sphericalDelta.set(0, 0, 0)

                scope._flickTbRadius = calculateTbRadius(
                    scope.camera,
                    scope._gizmoPosition,
                    scope.radiusFactor
                )

                // scale = 1

                // update condition is:
                // min(camera displacement, camera rotation in radians)^2 > EPS
                // using small-angle approximation cos(x/2) = 1 - x^2 / 8

                if (
                    zoomChanged ||
                    lastPosition.distanceToSquared(scope.camera.position) >
                        EPS ||
                    8 * (1 - lastQuaternion.dot(scope.camera.quaternion)) > EPS
                ) {
                    scope.dispatchEvent(_changeEvent)

                    lastPosition.copy(scope.camera.position)
                    lastQuaternion.copy(scope.camera.quaternion)
                    zoomChanged = false

                    return true
                }

                return false
            }
        })()

        this.dispose = function () {
            scope.domElement.removeEventListener("contextmenu", onContextMenu)

            scope.domElement.removeEventListener("pointerdown", onPointerDown)
            scope.domElement.removeEventListener(
                "pointercancel",
                onPointerCancel
            )
            scope.domElement.removeEventListener("wheel", onMouseWheel)

            scope.domElement.removeEventListener("pointermove", onPointerMove)
            scope.domElement.removeEventListener("pointerup", onPointerUp)

            if (scope._domElementKeyEvents !== null) {
                scope._domElementKeyEvents.removeEventListener(
                    "keydown",
                    onKeyDown
                )
            }

            // scope.dispatchEvent( { type: 'dispose' } ); // should this be added here?
        }

        //
        // internals
        //

        const scope = this

        const trackData = {
            distance: 100,
            angle: 0,
            thetaOffset: 0,
        }

        let extraThetaOffset = 0
        let extraThetaOffsetDelta = 0

        // this.updateMaxThetaOffset();

        this._state = STATE.NONE

        const EPS = 0.000001

        // let scale = 1

        let trackDelta = 0
        let minDistanceDelta = 0
        var focusRadiusDelta = 0
        const panOffset = new Vector3()
        let zoomChanged = false

        const rotateStart = new Vector2()
        const rotateEnd = new Vector2()
        const rotateDelta = new Vector2()

        const panStart = new Vector2()
        const panEnd = new Vector2()
        const panDelta = new Vector2()

        const dollyStart = new Vector2()
        this._dollyStartDist = 0
        const dollyEnd = new Vector2()
        const dollyDelta = new Vector2()

        const pointers = []
        const pointerPositions = {}
        this._pointersLengthPrev = 0

        function getAutoRotationAngle() {
            return ((2 * Math.PI) / 60 / 60) * scope.autoRotateSpeed
        }

        function getZoomScale() {
            return scope.zoomSpeed
        }

        const panLeft = (function () {
            const v = new Vector3()

            return function panLeft(distance, objectMatrix) {
                v.setFromMatrixColumn(objectMatrix, 0) // get X column of objectMatrix
                v.multiplyScalar(-distance)

                panOffset.add(v)
            }
        })()

        const panUp = (function () {
            const v = new Vector3()

            return function panUp(distance, objectMatrix) {
                if (scope.screenSpacePanning === true) {
                    v.setFromMatrixColumn(objectMatrix, 1)
                } else {
                    v.setFromMatrixColumn(objectMatrix, 0)
                    v.crossVectors(scope.camera.up, v)
                }

                v.multiplyScalar(distance)

                panOffset.add(v)
            }
        })()

        // deltaX and deltaY are in pixels; right and down are positive
        const pan = (function () {
            const offset = new Vector3()

            return function pan(deltaX, deltaY) {
                const element = scope.domElement

                if (scope.camera.isPerspectiveCamera) {
                    // perspective
                    const position = scope.camera.position
                    offset.copy(position).sub(scope.target)
                    let targetDistance = offset.length()

                    // half of the fov is center to top of screen
                    targetDistance *= Math.tan(
                        ((scope.camera.fov / 2) * Math.PI) / 180.0
                    )

                    // we use only clientHeight here so aspect ratio does not distort speed
                    panLeft(
                        (2 * deltaX * targetDistance) / element.clientHeight,
                        scope.camera.matrix
                    )
                    panUp(
                        (2 * deltaY * targetDistance) / element.clientHeight,
                        scope.camera.matrix
                    )
                } else if (scope.camera.isOrthographicCamera) {
                    // orthographic
                    panLeft(
                        (deltaX * (scope.camera.right - scope.camera.left)) /
                            scope.camera.zoom /
                            element.clientWidth,
                        scope.camera.matrix
                    )
                    panUp(
                        (deltaY * (scope.camera.top - scope.camera.bottom)) /
                            scope.camera.zoom /
                            element.clientHeight,
                        scope.camera.matrix
                    )
                } else {
                    // camera neither orthographic nor perspective
                    console.warn(
                        "WARNING: OrbitControls.js encountered an unknown camera type - pan disabled."
                    )
                    scope.enablePan = false
                }
            }
        })()

        function dollyOut(dollyScale) {
            if (scope.camera.isPerspectiveCamera) {
                const nextDist = scope._dollyStartDist * (1 / dollyScale)
                const nextTrack = scope.invertCameraTrack(
                    nextDist + scope._tbRadius
                )
                const nextDelta = nextTrack - scope.trackPos
                console.log(dollyScale, scope._tbRadius)
                // const nextDelta = trackDelta + (dollyScale - 1)
                const minDelta = scope.trackPos * -1
                const maxDelta = 1 - scope.trackPos
                const clampedDelta = Math.min(
                    maxDelta,
                    Math.max(minDelta, nextDelta)
                )
                // trackDelta += (dollyScale - 1);
                trackDelta = clampedDelta
            } else if (scope.camera.isOrthographicCamera) {
                scope.camera.zoom = Math.max(
                    scope.minZoom,
                    Math.min(scope.maxZoom, scope.camera.zoom * dollyScale)
                )
                scope.camera.updateProjectionMatrix()
                zoomChanged = true
            } else {
                console.warn(
                    "WARNING: OrbitControls.js encountered an unknown camera type - dolly/zoom disabled."
                )
                scope.enableZoom = false
            }
        }

        function dollyIn(dollyScale) {
            if (scope.camera.isPerspectiveCamera) {
                const nextDelta = trackDelta - (dollyScale - 1)
                const minDelta = scope.trackPos * -1
                const maxDelta = 1 - scope.trackPos
                const clampedDelta = Math.min(
                    maxDelta,
                    Math.max(minDelta, nextDelta)
                )
                // trackDelta += (dollyScale - 1);
                trackDelta = clampedDelta
            } else if (scope.camera.isOrthographicCamera) {
                scope.camera.zoom = Math.max(
                    scope.minZoom,
                    Math.min(scope.maxZoom, scope.camera.zoom / dollyScale)
                )
                scope.camera.updateProjectionMatrix()
                zoomChanged = true
            } else {
                console.warn(
                    "WARNING: OrbitControls.js encountered an unknown camera type - dolly/zoom disabled."
                )
                scope.enableZoom = false
            }
        }

        //
        // event callbacks - update the object state
        //

        function handleMouseDownRotate(event) {
            scope.setCenter(event.clientX, event.clientY)
            updateStartArcballCursorPosition(scope._center.x, scope._center.y)
        }

        function handleMouseDownDolly(event) {
            dollyStart.set(event.clientX, event.clientY)
        }

        function handleMouseDownPan(event) {
            panStart.set(event.clientX, event.clientY)
        }

        function handleMouseMoveRotate(event) {
            // scope.setCenter(event.clientX, event.clientY)
            updateCurrentArcballCursorPosition(scope._center.x, scope._center.y)

            {
                const distance = scope._startCursorPosition.distanceTo(
                    scope._currentCursorPosition
                )
                const angle = scope._startCursorPosition.angleTo(
                    scope._currentCursorPosition
                )
                const amount = Math.max(distance / scope._tbRadius, angle) // effective rotation angle

                rotateDelta.subVectors(
                    scope._currentCursorPosition,
                    scope._startCursorPosition
                )
                rotateDelta.normalize()
                rotateDelta.multiplyScalar(amount)
            }

            const rotateDeltaFlick = scope._v2_1
            {
                const distance = scope._startCursorPositionFlick.distanceTo(
                    scope._currentCursorPositionFlick
                )
                const angle = scope._startCursorPositionFlick.angleTo(
                    scope._currentCursorPositionFlick
                )
                const amount = Math.max(distance / scope._flickTbRadius, angle) // effective rotation angle

                rotateDeltaFlick.subVectors(
                    scope._currentCursorPositionFlick,
                    scope._startCursorPositionFlick
                )
                rotateDeltaFlick.normalize()
                rotateDeltaFlick.multiplyScalar(amount)
            }

            scope._startCursorPosition.copy(scope._currentCursorPosition)
            scope._startCursorPositionFlick.copy(
                scope._currentCursorPositionFlick
            )

            rotateDelta.y *= -1
            rotateDeltaFlick.y *= -1

            const latCircumference =
                Math.sin((90 - Math.abs(scope._lngLat.y)) * DEG2RAD) *
                scope._tbRadius *
                2 *
                Math.PI
            const equatorCircumference = scope._tbRadius * 2 * Math.PI

            let rotateLeftFactor = latCircumference / equatorCircumference
            if (rotateLeftFactor < 0.4) {
                // Workaround for weird bug
                rotateLeftFactor = 1
            }

            scope.rotateLeft(rotateDelta.x / rotateLeftFactor)
            scope.rotateUp(rotateDelta.y)

            rotateStart.copy(rotateEnd)

            if (scope.enableAnimations) {
                const horizontalSpeed = rotateDelta.x / rotateLeftFactor
                const verticalSpeed = rotateDelta.y

                scope._timePrev = scope._timeCurrent
                scope._timeCurrent = performance.now()
                // scope._anglePrev = scope._angleCurrent;
                // scope._angleCurrent = amount;
                scope._cursorPosPrev.copy(scope._cursorPosCurr)
                scope._cursorPosCurr.copy(scope._currentCursorPosition)

                scope._wPrev.copy(scope._wCurr)
                scope._wCurr.set(horizontalSpeed, verticalSpeed)

                const horizontalFlickSpeed =
                    rotateDeltaFlick.x / rotateLeftFactor
                const verticalFlickSpeed = rotateDeltaFlick.y
                scope._wFlickPrev.copy(scope._wFlickCurr)
                scope._wFlickCurr.set(horizontalFlickSpeed, verticalFlickSpeed)
            }

            scope.update()
        }

        function handleMouseMoveDolly(event) {
            dollyEnd.set(event.clientX, event.clientY)

            dollyDelta.subVectors(dollyEnd, dollyStart)

            if (dollyDelta.y > 0) {
                dollyOut(getZoomScale())
            } else if (dollyDelta.y < 0) {
                dollyIn(getZoomScale())
            }

            dollyStart.copy(dollyEnd)

            scope.update()
        }

        function handleMouseMovePan(event) {
            panEnd.set(event.clientX, event.clientY)

            panDelta.subVectors(panEnd, panStart).multiplyScalar(scope.panSpeed)

            pan(panDelta.x, panDelta.y)

            panStart.copy(panEnd)

            scope.update()
        }

        function handleMouseWheel(event) {
            if (event.deltaY !== 0) {
                dollyIn((getZoomScale() * event.deltaY) / 4 / (53 * 10) + 1)
            }

            // if ( event.deltaY < 0 ) {

            // 	dollyIn( getZoomScale() );

            // } else if ( event.deltaY > 0 ) {

            // 	dollyOut( getZoomScale() );

            // }

            scope.update()
        }

        function handleKeyDown(event) {
            let needsUpdate = false

            switch (event.code) {
                case scope.keys.UP:
                    pan(0, scope.keyPanSpeed)
                    needsUpdate = true
                    break

                case scope.keys.BOTTOM:
                    pan(0, -scope.keyPanSpeed)
                    needsUpdate = true
                    break

                case scope.keys.LEFT:
                    pan(scope.keyPanSpeed, 0)
                    needsUpdate = true
                    break

                case scope.keys.RIGHT:
                    pan(-scope.keyPanSpeed, 0)
                    needsUpdate = true
                    break
            }

            if (needsUpdate) {
                // prevent the browser from scrolling on cursor keys
                event.preventDefault()

                scope.update()
            }
        }

        function handleTouchStartRotate() {
            if (pointers.length === 1) {
                scope.setCenter(pointers[0].clientX, pointers[0].clientY)
            } else {
                const x = 0.5 * (pointers[0].clientX + pointers[1].clientX)
                const y = 0.5 * (pointers[0].clientY + pointers[1].clientY)

                scope.setCenter(x, y)
            }

            updateStartArcballCursorPosition(scope._center.x, scope._center.y)
            updateCurrentArcballCursorPosition(scope._center.x, scope._center.y)
        }

        function handleTouchStartPan() {
            if (pointers.length === 1) {
                panStart.set(pointers[0].clientX, pointers[0].clientY)
            } else {
                const x = 0.5 * (pointers[0].clientX + pointers[1].clientX)
                const y = 0.5 * (pointers[0].clientY + pointers[1].clientY)

                panStart.set(x, y)
            }
        }

        function handleTouchStartDolly() {
            const dx = pointers[0].clientX - pointers[1].clientX
            const dy = pointers[0].clientY - pointers[1].clientY

            const distance = Math.sqrt(dx * dx + dy * dy)

            scope._dollyStartDist = trackData.distance - scope._tbRadius
            dollyStart.set(0, distance)
        }

        function handleTouchStartDollyPan() {
            if (scope.enableZoom) handleTouchStartDolly()

            if (scope.enablePan) handleTouchStartPan()
        }

        function handleTouchStartDollyRotate() {
            if (scope.enableZoom) handleTouchStartDolly()

            if (scope.enableRotate) handleTouchStartRotate()
        }

        /**
         *
         * @param {Number} x
         * @param {Number} y
         */
        function updateCurrentArcballCursorPosition(x, y) {
            const cameraDistance = scope.camera.position.distanceTo(
                scope._gizmoPosition
            )
            const cameraType = scope.camera.type
            const projectionMatrixInverse = scope.camera.projectionMatrixInverse

            scope._currentCursorPosition.copy(
                unprojectOnTbSurface(
                    projectionMatrixInverse,
                    cameraType,
                    cameraDistance,
                    x,
                    y,
                    scope.domElement,
                    scope._tbRadius,
                    scope._gizmoPosition
                )
            )
            scope._currentCursorPositionFlick.copy(
                unprojectOnTbSurface(
                    projectionMatrixInverse,
                    cameraType,
                    cameraDistance,
                    x,
                    y,
                    scope.domElement,
                    scope._flickTbRadius,
                    scope._gizmoPosition
                )
            )
        }

        /**
         *
         * @param {Number} x
         * @param {Number} y
         */
        function updateStartArcballCursorPosition(x, y) {
            const cameraDistance = scope.camera.position.distanceTo(
                scope._gizmoPosition
            )
            const cameraType = scope.camera.type
            const projectionMatrixInverse = scope.camera.projectionMatrixInverse

            scope._startCursorPosition.copy(
                unprojectOnTbSurface(
                    projectionMatrixInverse,
                    cameraType,
                    cameraDistance,
                    x,
                    y,
                    scope.domElement,
                    scope._tbRadius,
                    scope._gizmoPosition
                )
            )
            scope._startCursorPositionFlick.copy(
                unprojectOnTbSurface(
                    projectionMatrixInverse,
                    cameraType,
                    cameraDistance,
                    x,
                    y,
                    scope.domElement,
                    scope._flickTbRadius,
                    scope._gizmoPosition
                )
            )
        }

        function handleTouchMoveRotate(event) {
            // scope.setCenter(event.clientX, event.clientY)

            // if (pointers.length == 1) {

            // rotateEnd.set(event.clientX, event.clientY);

            // } else {

            // 	const position = getSecondPointerPosition(event);

            // 	const x = 0.5 * (event.clientX + position.x);
            // 	const y = 0.5 * (event.clientY + position.y);

            // rotateEnd.set(x, y);

            // }

            if (pointers.length === 1) {
                scope.setCenter(event.clientX, event.clientY)
            } else {
                const position = getSecondPointerPosition(event)

                const x = 0.5 * (event.clientX + position.x)
                const y = 0.5 * (event.clientY + position.y)

                scope.setCenter(x, y)

                if (scope._pointersLengthPrev === 1) {
                    // Prevent the view from "jumping" when a second pointer is added
                    updateStartArcballCursorPosition(
                        scope._center.x,
                        scope._center.y
                    )
                }
            }

            scope._pointersLengthPrev = pointers.length

            updateCurrentArcballCursorPosition(scope._center.x, scope._center.y)

            {
                const distance = scope._startCursorPosition.distanceTo(
                    scope._currentCursorPosition
                )
                const angle = scope._startCursorPosition.angleTo(
                    scope._currentCursorPosition
                )
                const amount = Math.max(distance / scope._tbRadius, angle) // effective rotation angle

                rotateDelta.subVectors(
                    scope._currentCursorPosition,
                    scope._startCursorPosition
                )
                rotateDelta.normalize()
                rotateDelta.multiplyScalar(amount)
            }

            const rotateDeltaFlick = scope._v2_1
            {
                const distance = scope._startCursorPositionFlick.distanceTo(
                    scope._currentCursorPositionFlick
                )
                const angle = scope._startCursorPositionFlick.angleTo(
                    scope._currentCursorPositionFlick
                )
                const amount = Math.max(distance / scope._flickTbRadius, angle) // effective rotation angle

                rotateDeltaFlick.subVectors(
                    scope._currentCursorPositionFlick,
                    scope._startCursorPositionFlick
                )
                rotateDeltaFlick.normalize()
                rotateDeltaFlick.multiplyScalar(amount)
            }

            scope._startCursorPosition.copy(scope._currentCursorPosition)
            scope._startCursorPositionFlick.copy(
                scope._currentCursorPositionFlick
            )

            rotateDelta.y *= -1
            rotateDeltaFlick.y *= -1

            const latCircumference =
                Math.sin((90 - Math.abs(scope._lngLat.y)) * DEG2RAD) *
                scope._tbRadius *
                2 *
                Math.PI
            const equatorCircumference = scope._tbRadius * 2 * Math.PI

            let rotateLeftFactor = latCircumference / equatorCircumference
            if (rotateLeftFactor < 0.4) {
                // Workaround for weird bug
                rotateLeftFactor = 1
            }

            scope.rotateLeft(rotateDelta.x / rotateLeftFactor)
            scope.rotateUp(rotateDelta.y)

            if (scope.enableAnimations) {
                const horizontalSpeed = rotateDelta.x / rotateLeftFactor
                const verticalSpeed = rotateDelta.y

                scope._timePrev = scope._timeCurrent
                scope._timeCurrent = performance.now()
                // scope._anglePrev = scope._angleCurrent;
                // scope._angleCurrent = amount;
                scope._cursorPosPrev.copy(scope._cursorPosCurr)
                scope._cursorPosCurr.copy(scope._currentCursorPosition)

                scope._wPrev.copy(scope._wCurr)
                scope._wCurr.set(horizontalSpeed, verticalSpeed)

                const horizontalFlickSpeed =
                    rotateDeltaFlick.x / rotateLeftFactor
                const verticalFlickSpeed = rotateDeltaFlick.y
                scope._wFlickPrev.copy(scope._wFlickCurr)
                scope._wFlickCurr.set(horizontalFlickSpeed, verticalFlickSpeed)
            }

            // rotateDelta.subVectors(rotateEnd, rotateStart).multiplyScalar(scope.rotateSpeed);

            // const element = scope.domElement;

            // rotateLeft(2 * Math.PI * rotateDelta.x / element.clientHeight); // yes, height

            // rotateUp(2 * Math.PI * rotateDelta.y / element.clientHeight);

            // rotateStart.copy(rotateEnd);
        }

        function handleTouchMovePan(event) {
            if (pointers.length === 1) {
                panEnd.set(event.clientX, event.clientY)
            } else {
                const position = getSecondPointerPosition(event)

                const x = 0.5 * (event.clientX + position.x)
                const y = 0.5 * (event.clientY + position.y)

                panEnd.set(x, y)
            }

            panDelta.subVectors(panEnd, panStart).multiplyScalar(scope.panSpeed)

            pan(panDelta.x, panDelta.y)

            panStart.copy(panEnd)
        }

        function handleTouchMoveDolly(event) {
            const position = getSecondPointerPosition(event)

            const dx = event.clientX - position.x
            const dy = event.clientY - position.y

            const distance = Math.sqrt(dx * dx + dy * dy)

            dollyEnd.set(0, distance)

            dollyDelta.set(
                0,
                Math.pow(dollyEnd.y / dollyStart.y, scope.zoomSpeed)
            )

            const nextDist = scope._dollyStartDist * (1 / dollyDelta.y)
            let nextTrack = scope.invertCameraTrack(nextDist + scope._tbRadius)
            if (isNaN(nextTrack)) {
                nextTrack = 1
            }
            const nextDelta = nextTrack - scope.trackPos
            // console.log(dollyScale, scope._tbRadius)
            // const nextDelta = trackDelta + (dollyScale - 1)
            const minDelta = scope.trackPos * -1
            const maxDelta = 1 - scope.trackPos
            const clampedDelta = Math.min(
                maxDelta,
                Math.max(minDelta, nextDelta)
            )
            // trackDelta += (dollyScale - 1);
            trackDelta = clampedDelta

            scope._dollyStartDist =
                scope.cameraTrack(nextTrack).distance - scope._tbRadius

            dollyStart.copy(dollyEnd)
        }

        function handleTouchMoveDollyPan(event) {
            if (scope.enableZoom) handleTouchMoveDolly(event)

            if (scope.enablePan) handleTouchMovePan(event)
        }

        function handleTouchMoveDollyRotate(event) {
            if (scope.enableZoom) handleTouchMoveDolly(event)

            if (scope.enableRotate) handleTouchMoveRotate(event)
        }

        //
        // event handlers - FSM: listen for events and reset state
        //

        function onPointerDown(event) {
            if (scope.enabled === false) return

            if (pointers.length === 0) {
                scope.domElement.setPointerCapture(event.pointerId)

                scope.domElement.addEventListener("pointermove", onPointerMove)
                scope.domElement.addEventListener("pointerup", onPointerUp)
            }

            //

            addPointer(event)

            if (event.pointerType === "touch") {
                onTouchStart(event)
            } else {
                onMouseDown(event)
            }
        }

        function onPointerMove(event) {
            if (scope.enabled === false) return

            // "break" any focus maneuver
            if (scope.isFocusing) {
                // scope.isFocusing = false
                scope.setFocusingAnimFlag(false)
                scope.setFocusingTrackFlag(false)
            }

            if (event.pointerType === "touch") {
                onTouchMove(event)
            } else {
                onMouseMove(event)
            }
        }

        function onPointerUp(event) {
            removePointer(event)

            if (pointers.length === 1) {
                console.log("transition to rotate")
                const pos = getSecondPointerPosition(event)
                scope.setCenter(pos.x, pos.y)
                // console.log(scope._center)
                updateCurrentArcballCursorPosition(
                    scope._center.x,
                    scope._center.y
                )
                updateStartArcballCursorPosition(
                    scope._center.x,
                    scope._center.y
                )
                scope._state = STATE.ROTATE
            }

            if (pointers.length === 0) {
                scope.onSinglePanEnd()
                scope.domElement.releasePointerCapture(event.pointerId)

                scope.domElement.removeEventListener(
                    "pointermove",
                    onPointerMove
                )
                scope.domElement.removeEventListener("pointerup", onPointerUp)
                scope._state = STATE.NONE
            }

            scope.dispatchEvent(_endEvent)
        }

        function onPointerCancel(event) {
            /**
             * For some reason, on chrome (and maybe others), when there's two touch pointers,
             * a single pointercancel event can invalidate BOTH of them.
             * I don't know if it's a browser bug or what, but for simplicity' sake,
             * we'll just consider a pointercancel event as invalidating all active pointers.
             */
            // TODO: Consider more complex cases, like multiple pointing devices
            removeAllPointers()
            scope.domElement.removeEventListener("pointermove", onPointerMove)
            scope.domElement.removeEventListener("pointerup", onPointerUp)
            scope._state = STATE.NONE
        }

        function onMouseDown(event) {
            let mouseAction

            switch (event.button) {
                case 0:
                    mouseAction = scope.mouseButtons.LEFT
                    break

                case 1:
                    mouseAction = scope.mouseButtons.MIDDLE
                    break

                case 2:
                    mouseAction = scope.mouseButtons.RIGHT
                    break

                default:
                    mouseAction = -1
            }

            switch (mouseAction) {
                case MOUSE.DOLLY:
                    if (scope.enableZoom === false) return

                    handleMouseDownDolly(event)

                    scope._state = STATE.DOLLY

                    break

                case MOUSE.ROTATE:
                    if (event.ctrlKey || event.metaKey || event.shiftKey) {
                        if (scope.enablePan === false) return

                        handleMouseDownPan(event)

                        scope._state = STATE.PAN
                    } else {
                        if (scope.enableRotate === false) return

                        handleMouseDownRotate(event)

                        scope._state = STATE.ROTATE
                    }

                    break

                case MOUSE.PAN:
                    if (event.ctrlKey || event.metaKey || event.shiftKey) {
                        if (scope.enableRotate === false) return

                        handleMouseDownRotate(event)

                        scope._state = STATE.ROTATE
                    } else {
                        if (scope.enablePan === false) return

                        handleMouseDownPan(event)

                        scope._state = STATE.PAN
                    }

                    break

                default:
                    scope._state = STATE.NONE
            }

            if (scope._state !== STATE.NONE) {
                scope.dispatchEvent(_startEvent)
            }
        }

        function onMouseMove(event) {
            scope.setCenter(event.clientX, event.clientY)

            // State is getting set to -1 here mid-rotate

            if (scope.enabled === false) return

            switch (scope._state) {
                case STATE.ROTATE:
                    if (scope.enableRotate === false) return

                    handleMouseMoveRotate(event)

                    break

                case STATE.DOLLY:
                    if (scope.enableZoom === false) return

                    handleMouseMoveDolly(event)

                    break

                case STATE.PAN:
                    if (scope.enablePan === false) return

                    handleMouseMovePan(event)

                    break
            }
        }

        function onMouseWheel(event) {
            scope.setCenter(event.clientX, event.clientY)
            if (
                scope.enabled === false ||
                scope.enableZoom === false ||
                scope._state !== STATE.NONE
            )
                return

            event.preventDefault()

            scope.dispatchEvent(_startEvent)

            console.log("wheel")

            handleMouseWheel(event)

            scope.dispatchEvent(_endEvent)
        }

        function onKeyDown(event) {
            if (scope.enabled === false || scope.enablePan === false) return

            handleKeyDown(event)
        }

        function onTouchStart(event) {
            trackPointer(event)

            // When the first touch is detected, switch from mouse params to touch params
            if (
                scope.startRotating === scope.startRotatingInitialMouse &&
                scope.endRotating === scope.endRotatingInitialMouse
            ) {
                scope.startRotating = scope.startRotatingInitialTouch
                scope.endRotating = scope.endRotatingInitialTouch
            }

            switch (pointers.length) {
                case 1:
                    switch (scope.touches.ONE) {
                        case TOUCH.ROTATE:
                            if (scope.enableRotate === false) return

                            handleTouchStartRotate()

                            scope._state = STATE.ROTATE

                            break

                        case TOUCH.PAN:
                            if (scope.enablePan === false) return

                            handleTouchStartPan()

                            scope._state = STATE.TOUCH_PAN

                            break

                        default:
                            scope._state = STATE.NONE
                    }

                    break

                case 2:
                    switch (scope.touches.TWO) {
                        case TOUCH.DOLLY_PAN:
                            if (
                                scope.enableZoom === false &&
                                scope.enablePan === false
                            )
                                return

                            handleTouchStartDollyPan()

                            scope._state = STATE.TOUCH_DOLLY_PAN

                            break

                        case TOUCH.DOLLY_ROTATE:
                            if (
                                scope.enableZoom === false &&
                                scope.enableRotate === false
                            )
                                return

                            handleTouchStartDollyRotate()

                            scope._state = STATE.TOUCH_DOLLY_ROTATE

                            break

                        default:
                            scope._state = STATE.NONE
                    }

                    break

                default:
                    scope._state = STATE.NONE
            }

            if (scope._state !== STATE.NONE) {
                scope.dispatchEvent(_startEvent)
            }
        }

        function onTouchMove(event) {
            trackPointer(event)

            switch (scope._state) {
                case STATE.ROTATE:
                    if (scope.enableRotate === false) return

                    handleTouchMoveRotate(event)

                    scope.update()

                    break

                case STATE.TOUCH_PAN:
                    if (scope.enablePan === false) return

                    handleTouchMovePan(event)

                    scope.update()

                    break

                case STATE.TOUCH_DOLLY_PAN:
                    if (scope.enableZoom === false && scope.enablePan === false)
                        return

                    handleTouchMoveDollyPan(event)

                    scope.update()

                    break

                case STATE.TOUCH_DOLLY_ROTATE:
                    if (
                        scope.enableZoom === false &&
                        scope.enableRotate === false
                    )
                        return

                    handleTouchMoveDollyRotate(event)

                    scope.update()

                    break

                default:
                    scope._state = STATE.NONE
            }
        }

        function onContextMenu(event) {
            // if (scope.enabled === false) return
            // event.preventDefault();
        }

        function addPointer(event) {
            pointers.push(event)
        }

        function removePointer(event) {
            delete pointerPositions[event.pointerId]

            for (let i = 0; i < pointers.length; i++) {
                if (pointers[i].pointerId === event.pointerId) {
                    pointers.splice(i, 1)
                    return
                }
            }
        }

        function removeAllPointers() {
            // Not doing
            // pointerPositions = {}
            // pointers = []
            // because I don't want to make these mutable
            for (let i = 0; i < pointers.length; i++) {
                delete pointerPositions[i]
            }
            pointers.length = 0
        }

        function trackPointer(event) {
            let position = pointerPositions[event.pointerId]

            if (position === undefined) {
                position = new Vector2()
                pointerPositions[event.pointerId] = position
            }

            position.set(event.clientX, event.clientY)
        }

        function getSecondPointerPosition(event) {
            const pointer =
                event.pointerId === pointers[0].pointerId
                    ? pointers[1]
                    : pointers[0]

            return pointerPositions[pointer.pointerId]
        }

        //

        scope.domElement.addEventListener("contextmenu", onContextMenu)

        scope.domElement.addEventListener("pointerdown", onPointerDown)
        // scope.domElement.addEventListener("pointermove", (event) => {
        // 	scope.setCenter(event.clientX, event.clientY)
        // })
        scope.domElement.addEventListener("pointercancel", onPointerCancel)
        scope.domElement.addEventListener("wheel", onMouseWheel, {
            passive: false,
        })

        // force an update at start

        this.update()
    }

    setTBRadius(radius) {
        this._tbRadius = radius
    }

    rotateLeft(angle) {
        this._sphericalDelta.theta -= angle
    }

    rotateUp(angle) {
        this._sphericalDelta.phi -= angle
    }

    /**
     * Update the trackball FSA
     * @param {STATE} newState New state of the FSA
     * @param {Boolean} updateMatrices If matriices state should be updated
     */
    updateTbState(newState, updateMatrices) {
        this._state = newState
        // if ( updateMatrices ) {

        // 	this.updateMatrixState();

        // }
    }

    /**
     *
     * @param {*} time =
     * @param {Vector2} start start position
     * @param {Vector2} end end position
     */

    onFocusAnim(time, start, end) {
        this.setFocusingAnimFlag(true)
        if (this._timeStart === -1) {
            // animation start
            // this._anglePrev = 0;
            this._angleCurrent = 0
            this._timeStart = time
        }

        if (this._state === STATE.ANIMATION_FOCUS) {
            // w = w0 + alpha * t
            const deltaTime = (time - this._timeStart) / 1000
            // const wHorizontal = w0Horizontal - (w0Horizontal * (this.dampingFactor))
            // const wVertical = w0Vertical - (w0Vertical * (this.dampingFactor))

            // TODO: change for vector and diminish magnitude

            const offset0 = this._v2_1.copy(end).sub(start)
            const d0 = offset0.length()

            const normalizedTime = deltaTime / this.focusDuration

            // Ease out: 1 - (1 - x)^2
            const easedTime = 1 - Math.pow(1 - normalizedTime, 3)
            const d = easedTime * d0

            // const v = angularSpeed0 + ( ( - this.tbDampingFactor ) * deltaTime );

            // const wHorizontal = w0Horizontal + ( ( - this.tbDampingFactor ) * deltaTime );
            // const wVertical = w0Vertical + ( ( - this.tbDampingFactor ) * deltaTime );

            if (normalizedTime < 1) {
                // console.log({d, d0})

                // tetha = 0.5 * alpha * t^2 + w0 * t + tetha0
                // const horizontalCurrent = 0.5 * ( - this.tbDampingFactor ) * Math.pow( deltaTime, 2 ) + w0Horizontal * deltaTime + 0;
                // const verticalCurrent = 0.5 * ( - this.tbDampingFactor ) * Math.pow( deltaTime, 2 ) + w0Vertical * deltaTime + 0;

                // const horizontalCurrent = w0.x - (this.tbDampingFactor * deltaTime)
                // const verticalCurrent = w0.y - (this.tbDampingFactor * deltaTime)

                const offset = offset0.normalize().multiplyScalar(d).add(start)

                // console.log(wHorizontal)
                // console.log(horizontalCurrent)

                // this.applyTransformMatrix( this.rotate( rotationAxis, this._angleCurrent ) );
                this._spherical.theta = offset.x
                this._spherical.phi = offset.y
                this.dispatchEvent(_changeEvent)
                const self = this
                this._animationId = window.requestAnimationFrame(function (t) {
                    self.onFocusAnim(t, start, end)
                })
            } else {
                this.setFocusingAnimFlag(false)

                this._animationId = -1
                this._timeStart = -1

                this.updateTbState(STATE.NONE, false)
                // this.activateGizmos( false );

                this.dispatchEvent(_changeEvent)
            }
        } else {
            // interrupt animation

            this.setFocusingAnimFlag(false)

            this._animationId = -1
            this._timeStart = -1

            if (this._state !== STATE.ROTATE) {
                // this.activateGizmos( false );
                this.dispatchEvent(_changeEvent)
            }
        }
    }

    setFocusingAnimFlag(value) {
        this._isFocusAnim = value
        this.isFocusing = this._isFocusAnim || this._isFocusTrack
    }

    setFocusingTrackFlag(value) {
        this._isFocusTrack = value
        this.isFocusing = this._isFocusAnim || this._isFocusTrack
    }

    onSinglePanEnd() {
        if (this._state === STATE.ROTATE) {
            if (!this.enableRotate) {
                return
            }

            if (this.enableAnimations) {
                // perform rotation animation
                const deltaTime = performance.now() - this._timeCurrent
                if (deltaTime < 120) {
                    // const wHorizontal = ( this._wHorizontalPrev + this._wHorizontalCurr ) / 2;
                    // const wVertical = ( this._wVerticalPrev + this._wVerticalCurr ) / 2;

                    this._v2_1
                        .addVectors(this._wFlickCurr, this._wFlickPrev)
                        .multiplyScalar(0.5)
                    // this._v2_1.clampScalar(-Infinity, this.wMax)
                    const flickSpeedRaw = this._v2_1.length()
                    const flickSpeed = deadzoneClamp(
                        flickSpeedRaw,
                        -this.wMax,
                        this.wMax,
                        this.flickDeadZoneRadius
                    )

                    // Average vectors
                    this._v2_1
                        .addVectors(this._wCurr, this._wPrev)
                        .multiplyScalar(0.5)
                    let angularSpeed = this._v2_1.length()
                    const direction = new Vector2().copy(this._v2_1).normalize()

                    // Scale down angular speed
                    if (flickSpeedRaw > flickSpeed) {
                        angularSpeed =
                            angularSpeed * (flickSpeed / flickSpeedRaw)
                    }

                    const self = this
                    this._animationId = window.requestAnimationFrame(function (
                        t
                    ) {
                        self.updateTbState(STATE.ANIMATION_ROTATE, true)
                        // const rotationAxis = self.calculateRotationAxis( self._cursorPosPrev, self._cursorPosCurr );

                        // Don't pass in this._v2_1 because this is effectively asynchronous
                        self.onRotationAnim(
                            t,
                            angularSpeed,
                            direction,
                            flickSpeed
                        )
                    })
                } else {
                    // cursor has been standing still for over 120 ms since last movement
                    this.updateTbState(STATE.NONE, false)
                    // this.activateGizmos( false );
                    this.dispatchEvent(_changeEvent)
                }
            } else {
                this.updateTbState(STATE.NONE, false)
                // this.activateGizmos( false );
                this.dispatchEvent(_changeEvent)
            }
        } else if (this._state === STATE.PAN || this._state === STATE.NONE) {
            this.updateTbState(STATE.NONE, false)

            if (this.enableGrid) {
                // this.disposeGrid();
            }

            // this.activateGizmos( false );
            this.dispatchEvent(_changeEvent)
        }

        this.dispatchEvent(_endEvent)
    }
}

/**
 * Convert from position on arcball surface to longitude and latitude
 * @param {Vector3} arcballPos
 * @param {Vector2} cameraLngLat
 * @param {{tmpSpherical: Spherical, extraThetaOffset: number, out: Vector2}} param2
 * @return {Vector2}
 */
function arcballPosToMouseLngLat(
    arcballPos,
    cameraLngLat,
    { tmpSpherical, extraThetaOffset, out }
) {
    tmpSpherical.setFromVector3(arcballPos)
    const mouseLng =
        globeAzimuthalToLng(tmpSpherical.theta) + 90 + cameraLngLat.x
    const mouseLat =
        globePolarToLat(tmpSpherical.phi, extraThetaOffset) + cameraLngLat.y
    out.set(mouseLng, mouseLat)
    return out
}

/**
 * Determine camera pos necessary to line up a certain arcball mouse position with a given lat/lng point
 * @param {Vector3} arcballPos
 * @param {Vector2} mouseLngLat
 * @param {{tmpSpherical: Spherical, extraThetaOffset: number, out: Vector2}} param2
 * @return {Vector2}
 */
function arcballPosToCameraLngLat(
    arcballPos,
    mouseLngLat,
    { tmpSpherical, extraThetaOffset, out }
) {
    tmpSpherical.setFromVector3(arcballPos)
    const cameraLng =
        mouseLngLat.x - (globeAzimuthalToLng(tmpSpherical.theta) + 90)
    const cameraLat =
        mouseLngLat.y - globePolarToLat(tmpSpherical.phi, extraThetaOffset)
    out.set(cameraLng, cameraLat)
    return out
}

function clamp(x, min, max) {
    return Math.min(Math.max(x, min), max)
}

/**
 *
 * @param {Number} x
 * @param {Number} radius
 */
function deadzone(x, radius) {
    if (Math.abs(x) < radius) {
        return 0
    }
    return x
}

function deadzoneClamp(x, min, max, deadzoneRadius) {
    return deadzone(clamp(x, min, max), deadzoneRadius)
}

/**
 * Unproject the cursor on the trackball surface
 * @param {THREE.Matrix4} projectionMatrixInverse
 * @param {String} cameraType
 * @param {Number} cameraDistance
 * @param {Number} cursorX Cursor horizontal coordinate on screen
 * @param {Number} cursorY Cursor vertical coordinate on screen
 * @param {HTMLElement} canvas The canvas where the renderer draws its output
 * @param {number} tbRadius The trackball radius
 * @param {Vector3} gizmoPosition The gizmo position
 * @param {{v2_1: Vector2 | undefined, v3_1: Vector3 | undefined}} scratch Scratch values to use in computing and return, for performance. If not passed in, new objects will be created
 * @returns {Vector3} The unprojected point on the trackball surface
 */
// TODO: Adapt this to take into account camera tilt?
function unprojectOnTbSurface(
    projectionMatrixInverse,
    cameraType,
    cameraDistance,
    cursorX,
    cursorY,
    canvas,
    tbRadius,
    gizmoPosition,
    { v2_1: v2param, v3_1: v3param } = {}
) {
    const v2 = v2param !== undefined ? v2param : new Vector2()
    const v3 = v3param !== undefined ? v3param : new Vector3()

    if (cameraType === "OrthographicCamera") {
        v2.copy(getCursorPosition(cursorX, cursorY, canvas))
        v3.set(v2.x, v2.y, 0)

        const x2 = Math.pow(v2.x, 2)
        const y2 = Math.pow(v2.y, 2)
        const r2 = Math.pow(this._tbRadius, 2)

        if (x2 + y2 <= r2 * 0.5) {
            // intersection with sphere
            v3.setZ(Math.sqrt(r2 - (x2 + y2)))
        } else {
            // intersection with hyperboloid
            v3.setZ((r2 * 0.5) / Math.sqrt(x2 + y2))
        }

        return v3
    } else if (cameraType === "PerspectiveCamera") {
        // unproject cursor on the near plane
        getCursorNDC(cursorX, cursorY, canvas, v2)

        v3.set(v2.x, v2.y, -1)
        v3.applyMatrix4(projectionMatrixInverse)

        const rayDir = v3.clone().normalize() // unprojected ray direction
        const cameraGizmoDistance = cameraDistance
        const radius2 = Math.pow(tbRadius, 2)

        //	  camera
        //		|\
        //		| \
        //		|  \
        //	h	|	\
        //		| 	 \
        //		| 	  \
        //	_ _ | _ _ _\ _ _  near plane
        //			l

        const h = v3.z
        const l = Math.sqrt(Math.pow(v3.x, 2) + Math.pow(v3.y, 2))

        if (l === 0) {
            // ray aligned with camera
            rayDir.set(v3.x, v3.y, tbRadius)
            return rayDir
        }

        const m = h / l
        const q = cameraGizmoDistance

        /*
         * calculate intersection point between unprojected ray and trackball surface
         *|y = m * x + q
         *|x^2 + y^2 = r^2
         *
         * (m^2 + 1) * x^2 + (2 * m * q) * x + q^2 - r^2 = 0
         */
        let a = Math.pow(m, 2) + 1
        let b = 2 * m * q
        let c = Math.pow(q, 2) - radius2
        let delta = Math.pow(b, 2) - 4 * a * c

        if (delta >= 0) {
            // intersection with sphere
            v2.setX((-b - Math.sqrt(delta)) / (2 * a))
            v2.setY(m * v2.x + q)

            const angle = RAD2DEG * v2.angle()

            if (angle >= 45) {
                // if angle between intersection point and X' axis is >= 45°, return that point
                // otherwise, calculate intersection point with hyperboloid

                const rayLength = Math.sqrt(
                    Math.pow(v2.x, 2) + Math.pow(cameraGizmoDistance - v2.y, 2)
                )
                rayDir.multiplyScalar(rayLength)
                rayDir.z += cameraGizmoDistance
                return rayDir
            }
        }

        // intersection with hyperboloid
        /*
         *|y = m * x + q
         *|y = (1 / x) * (r^2 / 2)
         *
         * m * x^2 + q * x - r^2 / 2 = 0
         */

        a = m
        b = q
        c = -radius2 * 0.5
        delta = Math.pow(b, 2) - 4 * a * c
        v2.setX((-b - Math.sqrt(delta)) / (2 * a))
        v2.setY(m * v2.x + q)

        const rayLength = Math.sqrt(
            Math.pow(v2.x, 2) + Math.pow(cameraGizmoDistance - v2.y, 2)
        )

        rayDir.multiplyScalar(rayLength)
        rayDir.z += cameraGizmoDistance
        return rayDir
    }
}

/**
 * Calculate the cursor position in NDC
 * @param {number} x Cursor horizontal coordinate within the canvas
 * @param {number} y Cursor vertical coordinate within the canvas
 * @param {HTMLElement} canvas The canvas where the renderer draws its output
 * @param {Vector2 | undefined} out Optional vector to use for output. If this is undefined, a new Vector2 is constructed
 * @returns {Vector2} Cursor normalized position inside the canvas
 */
function getCursorNDC(cursorX, cursorY, canvas, out) {
    const vec = out !== undefined ? out : new Vector2()
    const canvasRect = canvas.getBoundingClientRect()
    vec.setX(((cursorX - canvasRect.left) / canvasRect.width) * 2 - 1)
    vec.setY(((canvasRect.bottom - cursorY) / canvasRect.height) * 2 - 1)
    return vec
}

/**
 * Calculate the cursor position inside the canvas x/y coordinates with the origin being in the center of the canvas
 * @param {Number} x Cursor horizontal coordinate within the canvas
 * @param {Number} y Cursor vertical coordinate within the canvas
 * @param {HTMLElement} canvas The canvas where the renderer draws its output
 * @param {Vector2 | undefined} out Optional vector to use for output. If this is undefined, a new Vector2 is constructed
 * @returns {Vector2} Cursor position inside the canvas
 */
// ts-expect-error
function getCursorPosition(cursorX, cursorY, canvas, out) {
    const vec = out !== undefined ? out : new Vector2()
    vec.copy(this.getCursorNDC(cursorX, cursorY, canvas, out))
    vec.x *= (this.camera.right - this.camera.left) * 0.5
    vec.y *= (this.camera.top - this.camera.bottom) * 0.5
    return vec
}

/**
 * Calculate the trackball radius so that gizmo's diamater will be 2/3 of the minimum side of the camera frustum
 * @param {Camera} camera
 * @param {Vector3} gizmoPosition
 * @param {Number} radiusFactor
 * @returns {Number} The trackball radius
 */
function calculateTbRadius(camera, gizmoPosition, radiusFactor) {
    const distance = camera.position.distanceTo(gizmoPosition)

    if (camera.type === "PerspectiveCamera") {
        const halfFovV = DEG2RAD * camera.fov * 0.5 // vertical fov/2 in radians
        const halfFovH = Math.atan(camera.aspect * Math.tan(halfFovV)) // horizontal fov/2 in radians
        return Math.tan(Math.min(halfFovV, halfFovH)) * distance * radiusFactor
    } else if (camera.type === "OrthographicCamera") {
        return Math.min(camera.top, camera.right) * radiusFactor
    }
}

// This set of controls performs orbiting, dollying (zooming), and panning.
// Unlike TrackballControls, it maintains the "up" direction object.up (+Y by default).
// This is very similar to OrbitControls, another set of touch behavior
//
//    Orbit - right mouse, or left mouse + ctrl/meta/shiftKey / touch: two-finger rotate
//    Zoom - middle mouse, or mousewheel / touch: two-finger spread or squish
//    Pan - left mouse, or arrow keys / touch: one-finger move

class MapControls extends GlobeControls {
    constructor(object, domElement) {
        super(object, domElement)

        this.screenSpacePanning = false // pan orthogonal to world-space direction camera.up

        this.mouseButtons.LEFT = MOUSE.PAN
        this.mouseButtons.RIGHT = MOUSE.ROTATE

        this.touches.ONE = TOUCH.PAN
        this.touches.TWO = TOUCH.DOLLY_ROTATE
    }
}

export { GlobeControls, MapControls }
